import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import BrandLogo from "@/components/BrandLogo";
import { ArrowRight, ScanLine, CreditCard, ShieldCheck, ChefHat, Sparkles, ChevronRight, Calendar, Globe2, Network } from "lucide-react";

const FEATURES = [
  { icon: CreditCard, title: "Cashless wallet", desc: "Top up once, students tap & eat. No more lost lunch money." },
  { icon: ScanLine,   title: "RFID tap to pay", desc: "Sub-1.5s response. Meal plan validation with allergen alerts." },
  { icon: ShieldCheck,title: "Parental controls", desc: "Block allergens, junk-food limits, and see every meal." },
  { icon: Calendar,   title: "Meal plan & pre-order", desc: "Term-wise subscriptions, daily pre-orders, festival specials." },
];

const STATS = [
  { k: "2,000+", v: "students supported" },
  { k: "<1.5s", v: "RFID tap response" },
  { k: "99.5%", v: "school-hours uptime" },
  { k: "₹0",    v: "in lost cash" },
];

export default function Landing() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Subdomain & ?surface= redirect: e.g., smiqr.grubpe.com → /qr, smipos.grubpe.com → /login (POS), ?surface=qr|pos|hub for preview env
  useEffect(() => {
    const surface = params.get("surface");
    if (surface === "qr") return nav("/qr", { replace: true });
    if (surface === "pos") return nav("/login?next=/pos", { replace: true });
    if (surface === "hub") return nav("/login?next=/admin", { replace: true });
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    // Skip subdomain detection in Emergent preview environment so this landing remains reachable.
    if (host.includes("preview.emergent") || host.endsWith("emergentagent.com") || host === "localhost") return;
    const first = host.split(".")[0];
    // Production patterns: <slug>qr.grubpe.com, <slug>pos.grubpe.com, <slug>hub.grubpe.com
    if (first.endsWith("qr") && first !== "qr") return nav("/qr", { replace: true });
    if (first.endsWith("pos") && first !== "pos") return nav("/login?next=/pos", { replace: true });
    // hub → fall through to landing/login normally
  }, [params, nav]);

  return (
    <div className="min-h-screen bg-cream warm-grain">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-3">
        <Link to="/" data-testid="back-to-platform-logo" className="flex items-center">
          <BrandLogo size="md" />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/" data-testid="back-to-platform-link"
            className="hidden sm:inline-flex items-center gap-1 px-3 sm:px-4 py-2 text-sm font-semibold text-ink-700 hover:text-brand">
            ← All verticals
          </Link>
          <Link to="/login" data-testid="nav-login-btn" className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold bg-ink text-white hover:bg-ink-700 active:scale-95">
            <span>Sign in</span> <ArrowRight size={14} className="hidden sm:inline"/>
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold uppercase tracking-[0.18em] mb-6">
            <Sparkles size={14} /> Trusted across schools, colleges & food courts
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] text-ink">
            Lunchtime,<br/>
            <span className="text-brand">made simple.</span>
          </h1>
          <p className="mt-6 text-lg text-ink-600 max-w-xl leading-relaxed">
            Grubpe is the canteen platform powering schools and colleges — RFID tap-to-eat, meal plan subscriptions,
            pre-orders, parent wallets, dietary controls, and ERP integrations, all in one place.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/login" data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 shadow-soft">
              Get started <ArrowRight size={16} />
            </Link>
            <Link to="/login" data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-ink-300 text-ink font-semibold hover:border-ink">
              Try a demo account <ChevronRight size={16} />
            </Link>
          </div>

          <div className="mt-12 max-w-2xl" data-testid="hero-metrics">
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="relative rounded-3xl overflow-hidden shadow-floating">
            <img
              src="https://images.pexels.com/photos/2122294/pexels-photo-2122294.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=940"
              alt="Multi-outlet school canteen / college food court"
              className="w-full h-[480px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-medium">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center"><ChefHat size={18} /></div>
                <div>
                  <div className="font-heading text-lg font-bold text-ink">Multi-vendor canteen, single invoice</div>
                  <div className="text-sm text-ink-500">Spice Counter · Quick Bites · Sweet Treats</div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-6 -right-4 hidden md:block bg-sun text-ink rounded-2xl px-4 py-3 shadow-medium font-heading font-bold rotate-3">
            ₹450 wallet balance
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Five surfaces · One backend</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink mt-3">
            Everything your canteen needs — and nothing it doesn&apos;t.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-white rounded-3xl p-7 border border-border shadow-soft hover:shadow-medium transition-shadow">
                <div className="w-12 h-12 rounded-2xl bg-cream-200 flex items-center justify-center mb-5 text-brand">
                  <Icon size={22} strokeWidth={2.2} />
                </div>
                <div className="font-heading text-xl font-bold text-ink">{f.title}</div>
                <p className="text-sm text-ink-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Surfaces strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-ink text-white rounded-3xl p-8 relative overflow-hidden">
            <div className="text-xs uppercase tracking-[0.2em] text-sun font-bold">Web Hub</div>
            <div className="font-heading text-2xl font-bold mt-3">Parents in control</div>
            <p className="text-sm text-white/70 mt-2">Top-up wallet, dietary controls, meal plans, consumption reports.</p>
            <Link to="/login" className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-sun" data-testid="surface-parent-link">
              Open parent portal <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-brand text-white rounded-3xl p-8 relative overflow-hidden">
            <div className="text-xs uppercase tracking-[0.2em] text-sun font-bold">RFID · POS · Terminal</div>
            <div className="font-heading text-2xl font-bold mt-3">Tap. Validate. Serve.</div>
            <p className="text-sm text-white/80 mt-2">RFID meal plan validation, canteen monitoring terminal, real-time collection tracking.</p>
            <Link to="/login" className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-white" data-testid="surface-pos-link">
              Open Terminal <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-sun text-ink rounded-3xl p-8 relative overflow-hidden">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-700 font-bold">Meal Plans & Pre-orders</div>
            <div className="font-heading text-2xl font-bold mt-3">Subscribe. Pre-book. Save.</div>
            <p className="text-sm text-ink-700 mt-2">Term-wise meal plans, daily pre-orders, festival specials, all from the parent wallet.</p>
            <Link to="/login" className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-ink" data-testid="surface-plans-link">
              Get started <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-3xl border border-border p-8 sm:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Plays nicely with</div>
              <h3 className="font-heading text-3xl font-extrabold tracking-tight text-ink mt-2">EdKonnect, Cashfree, SendGrid, Karix</h3>
              <p className="text-sm text-ink-600 mt-3 max-w-md">SSO and master-data sync with EdKonnect. Real cashless top-ups via Cashfree. Daily reports via SendGrid email and Karix SMS for parents.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Network, label: "EdKonnect SSO", note: "School ERP bridge" },
                { icon: CreditCard, label: "Cashfree", note: "Wallet top-ups" },
                { icon: Globe2, label: "SendGrid", note: "Email reports" },
                { icon: ScanLine, label: "Karix", note: "SMS notifications" },
              ].map(it => {
                const I = it.icon;
                return (
                  <div key={it.label} className="bg-cream-100 rounded-2xl p-4 border border-border">
                    <I className="text-brand" size={22} />
                    <div className="font-heading font-bold text-ink mt-2 text-sm">{it.label}</div>
                    <div className="text-xs text-ink-500 mt-0.5">{it.note}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLogo size="sm" />
            <span className="text-xs text-ink-500">— School Canteen Management</span>
          </div>
          <div className="text-xs text-ink-500 flex items-center gap-2">
            Powered by <span className="font-heading font-extrabold text-ink">Intelvolks</span> · Used alongside <span className="font-heading font-bold">EdKonnect</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
